import { useState } from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";

import PageHeader from "components/PageHeader";

import useAccountingReportData from "layouts/tables/data/accountingReportData";
import { EXPENSE_CATEGORIES } from "layouts/tables/data/expenseTable";
import { resolveDateRangePreset } from "utils/dateRangePresets";

const money = (value) => `GHS ${Number(value || 0).toFixed(2)}`;

const REVENUE_LABELS = {
  tithe: "Tithe",
  harvest: "Harvest",
  offering: "Offering",
  welfare: "Welfare",
  dues: "Dues",
  contribution: "Contribution",
  seed: "Seed",
};

const revenueLabel = (type) => REVENUE_LABELS[type] || type.charAt(0).toUpperCase() + type.slice(1);

const categoryLabel = (value) => EXPENSE_CATEGORIES.find((c) => c.value === value)?.label || value;

function IncomeStatement() {
  const {
    report,
    isLoading,
    error,
    from,
    setFrom,
    to,
    setTo,
    handleApplyFilter,
    handleClearFilter,
    applyDateRange,
  } = useAccountingReportData();

  const [dateRangeType, setDateRangeType] = useState("custom");

  const handleDateRangeTypeChange = (e) => {
    const type = e.target.value;
    setDateRangeType(type);
    const range = resolveDateRangePreset(type);
    if (range) applyDateRange(range.from, range.to);
  };

  if (isLoading) {
    return <Typography sx={{ py: 6 }}>Loading income statement...</Typography>;
  }

  if (error) {
    return (
      <Typography color="error" sx={{ py: 6 }}>
        Error: {error}
      </Typography>
    );
  }

  const revenueLines = Object.entries(report?.income_by_type || {});
  const projectLines = report?.income_by_project || [];
  const expenseLines = Object.entries(report?.expense_by_category || {});
  const totalRevenue = report?.total_income ?? 0;
  const totalExpense = report?.total_expense ?? 0;
  const netIncome = report?.net ?? totalRevenue - totalExpense;

  return (
    <Box>
      <PageHeader
        title="Income Statement"
        subtitle="Revenue by type, expenses by category, and net income for the selected period."
        actions={
          <>
            <TextField
              select
              size="small"
              value={dateRangeType}
              onChange={handleDateRangeTypeChange}
              sx={{ width: 120 }}
            >
              <MenuItem value="daily">Daily</MenuItem>
              <MenuItem value="weekly">Weekly</MenuItem>
              <MenuItem value="monthly">Monthly</MenuItem>
              <MenuItem value="yearly">Yearly</MenuItem>
              <MenuItem value="custom">Custom Range</MenuItem>
            </TextField>

            {dateRangeType === "custom" && (
              <>
                <TextField
                  label="From"
                  type="date"
                  size="small"
                  value={from}
                  onChange={(e) => setFrom(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="To"
                  type="date"
                  size="small"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
                <Button variant="contained" onClick={handleApplyFilter}>
                  Update
                </Button>
              </>
            )}
            <Button variant="outlined" onClick={handleClearFilter}>
              Clear
            </Button>
            <Button variant="outlined" onClick={() => window.print()}>
              Print
            </Button>
          </>
        }
      />

      <Grid container justifyContent="center">
        <Grid item xs={12} md={8}>
          <Card sx={{ p: { xs: 2.5, md: 4 } }}>
            <Typography variant="h5" align="center" fontWeight="bold">
              Income Statement
            </Typography>
            <Typography variant="body2" align="center" color="text.secondary" mb={3}>
              {from || to ? `${from || "Start"} — ${to || "Present"}` : "All time"}
            </Typography>

            <Typography variant="subtitle2" fontWeight="bold" color="text.secondary" mb={1}>
              REVENUE
            </Typography>
            <Table size="small">
              <TableBody>
                {revenueLines.map(([type, amount]) => (
                  <TableRow key={type}>
                    <TableCell sx={{ border: 0, pl: 2 }}>{revenueLabel(type)}</TableCell>
                    <TableCell align="right" sx={{ border: 0 }}>
                      {money(amount)}
                    </TableCell>
                  </TableRow>
                ))}
                {revenueLines.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={2} align="center" sx={{ border: 0 }}>
                      No revenue recorded for this period.
                    </TableCell>
                  </TableRow>
                )}
                <TableRow>
                  <TableCell sx={{ borderTop: "1px solid", borderColor: "divider" }}>
                    <Typography fontWeight="bold">Total Revenue</Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ borderTop: "1px solid", borderColor: "divider" }}>
                    <Typography fontWeight="bold" color="success.main">
                      {money(totalRevenue)}
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>

            {projectLines.length > 0 && (
              <Box mt={4}>
                <Typography variant="subtitle2" fontWeight="bold" color="text.secondary" mb={1}>
                  REVENUE BY PROJECT
                </Typography>
                <Table size="small">
                  <TableBody>
                    {projectLines.map((p) => (
                      <TableRow key={p.project_id}>
                        <TableCell sx={{ border: 0, pl: 2 }}>{p.name}</TableCell>
                        <TableCell align="right" sx={{ border: 0 }}>
                          {money(p.total)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}

            <Box mt={4}>
              <Typography variant="subtitle2" fontWeight="bold" color="text.secondary" mb={1}>
                EXPENSES
              </Typography>
              <Table size="small">
                <TableBody>
                  {expenseLines.map(([category, amount]) => (
                    <TableRow key={category}>
                      <TableCell sx={{ border: 0, pl: 2 }}>{categoryLabel(category)}</TableCell>
                      <TableCell align="right" sx={{ border: 0 }}>
                        {money(amount)}
                      </TableCell>
                    </TableRow>
                  ))}
                  {expenseLines.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={2} align="center" sx={{ border: 0 }}>
                        No expenses recorded for this period.
                      </TableCell>
                    </TableRow>
                  )}
                  <TableRow>
                    <TableCell sx={{ borderTop: "1px solid", borderColor: "divider" }}>
                      <Typography fontWeight="bold">Total Expenses</Typography>
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ borderTop: "1px solid", borderColor: "divider" }}
                    >
                      <Typography fontWeight="bold" color="error.main">
                        {money(totalExpense)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">Net Income</Typography>
              <Typography
                variant="h5"
                fontWeight="bold"
                color={netIncome >= 0 ? "success.main" : "error.main"}
              >
                {money(netIncome)}
              </Typography>
            </Box>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

export default IncomeStatement;
